import React, { useEffect, useRef, useState } from "react";
import axiosInstance from "../../utils/axios";
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import './CreateEventPage.css';
import UnauthorizedPage from "../exceptions/UnauthorizedPage";

function CreateEventPage() {
    const startTimeRef = useRef(null);
    const endTimeRef = useRef(null);
    const startTimePickerRef = useRef(null);
    const endTimePickerRef = useRef(null);
    const [repeatOption, setRepeatOption] = useState("none");
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [isCheckingAuth, setIsCheckingAuth] = useState(true);

    const decodeJWT = (token) => {
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));
            return JSON.parse(jsonPayload);
        } catch (error) {
            console.error('Error decoding JWT:', error);
            return null;
        }
    };

    useEffect(() => {
        const token = localStorage.getItem('authToken');
        if (token) {
            const decoded = decodeJWT(token);
            if (decoded && decoded.role) {
                const userRole = String(decoded.role).toUpperCase();
                if (userRole === 'ADMIN') {
                    setIsAuthorized(true);
                }
            }
        }
        setIsCheckingAuth(false);
    }, []);

    useEffect(() => {
        if (!isAuthorized) return;

        if (startTimeRef.current && !startTimePickerRef.current) {
            startTimePickerRef.current = flatpickr(startTimeRef.current, {
                enableTime: true,
                dateFormat: "d.m.Y H:i",
                time_24hr: true,
                locale: {
                    firstDayOfWeek: 1
                }
            });
        }
        if (endTimeRef.current && !endTimePickerRef.current) {
            endTimePickerRef.current = flatpickr(endTimeRef.current, {
                enableTime: true,
                dateFormat: "d.m.Y H:i",
                time_24hr: true,
                locale: {
                    firstDayOfWeek: 1
                }
            });
        }

        // Cleanup function
        return () => {
            if (startTimePickerRef.current) {
                startTimePickerRef.current.destroy();
                startTimePickerRef.current = null;
            }
            if (endTimePickerRef.current) {
                endTimePickerRef.current.destroy();
                endTimePickerRef.current = null;
            }
        };
    }, [isAuthorized]);

    const parseDate = (dateString) => {
        const [datePart, timePart] = dateString.split(' ');
        const [day, month, year] = datePart.split('.');
        const [hour, minute] = timePart.split(':');
        return new Date(year, month - 1, day, hour, minute);
    };

    const formatDate = (date) => {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hour = String(date.getHours()).padStart(2, '0');
        const minute = String(date.getMinutes()).padStart(2, '0');
        return `${day}.${month}.${year} ${hour}:${minute}`;
    };

    const addWeeks = (date, weeks) => {
        const newDate = new Date(date);
        newDate.setDate(newDate.getDate() + (weeks * 7));
        return newDate;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const form = new FormData(e.target);
        const data = Object.fromEntries(form);

        try {
            if (repeatOption === "weekly") {
                const initialStartDate = parseDate(data.startTime);
                const initialEndDate = parseDate(data.endTime);
                
                const duration = initialEndDate.getTime() - initialStartDate.getTime();

                const promises = [];
                for (let i = 0; i < 4; i++) {
                    const eventData = { ...data };
                    const startDate = addWeeks(initialStartDate, i);
                    const endDate = new Date(startDate.getTime() + duration);
                    
                    eventData.startTime = formatDate(startDate);
                    eventData.endTime = formatDate(endDate);
                    
                    promises.push(axiosInstance.post("/api/events", eventData));
                }

                await Promise.all(promises);
                alert("4 üritust on edukalt loodud!");
            } else {
                await axiosInstance.post("/api/events", data);
                alert("Üritus on loodud!");
            }
            
            e.target.reset();
            setRepeatOption("none");
            
            if (startTimePickerRef.current) {
                startTimePickerRef.current.clear();
            }
            if (endTimePickerRef.current) {
                endTimePickerRef.current.clear();
            }
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.response?.data?.error || "Ürituse loomine ebaõnnestus";
            alert(errorMessage);
        }
    };

    if (isCheckingAuth) {
        return null;
    }

    if (!isAuthorized) {
        return <UnauthorizedPage />;
    }

    return (
        <form onSubmit={handleSubmit}>
            <input type="text" name="name" placeholder="Ürituse nimi" required />
            <input type="text" name="location" placeholder="Asukoht" />
            <input ref={startTimeRef} type="text" name="startTime" placeholder="algusaeg (pp.kk.aaaa hh:mm)" required/>
            <input ref={endTimeRef} type="text" name="endTime" placeholder="lõpuaeg (pp.kk.aaaa hh:mm)" required/>
            <input type="number" name="costKsv" placeholder="Osalustasu ksv!-le" step="0.01" min="0" />
            <input type="number" name="costKulaline" placeholder="Osalustasu külalisele" step="0.01" min="0" />
            <input type="number" name="costVil" placeholder="Osalustasu vil!-le" step="0.01" min="0" />
            <input type="number" name="costEiorliige" placeholder="Osalustasu noorliikmele" step="0.01" min="0" />
            <input type="number" name="participantLimit" placeholder="Osalejate piirarv" required min="1" />
            <select 
                name="repeat" 
                value={repeatOption} 
                onChange={(e) => setRepeatOption(e.target.value)}
                style={{ padding: '8px', fontSize: '14px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
            >
                <option value="none">Ei kordu</option>
                <option value="weekly">Korda nädalas (4 üritust)</option>
            </select>
            <button type="submit">Loo üritus</button>
        </form>
    );
}

export default CreateEventPage;
